import React, { useState, useEffect } from "react";
import { Card, CircularProgress } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import Table from "examples/Tables/Table";
import { useParams, useNavigate } from "react-router-dom";
import ArgonButton from "components/ArgonButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getAllRaiseInvoices } from "services/financeService";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { formatCycleLabel } from "utils/formatCycleLabel";

function InvoiceFullHistory() {
  const { parentId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [invoices, setInvoices] = useState([]);

  // Invoice Table Columns
  const columns = [
    { name: "S.No", align: "center" },
    // { name: "Date", align: "center" },
    { name: "Student Name", align: "left" },
    { name: "Invoice ID", align: "left" },
    { name: "Type", align: "left" },
    { name: "Total Sessions", align: "center" },
    { name: "Total Amount", align: "right" },
    { name: "Status", align: "center" },
    { name: "Payment Date", align: "center" },
  ];

  useEffect(() => {
    if (parentId) {
      fetchInvoiceHistory();
    }
  }, [parentId]);

  const fetchInvoiceHistory = async () => {
    try {
      setLoading(true);

      const safeNumber = (value) => {
        const num = Number(value);
        return Number.isFinite(num) ? num : 0;
      };

      const normalizeValue = (value) =>
        String(value ?? "")
          .trim()
          .toUpperCase();

      const getAllInvoiceEntries = (invoice) =>
        Array.isArray(invoice?.invoices) && invoice.invoices.length > 0
          ? invoice.invoices
          : [invoice];

      const formatInvoiceType = (entry) => {
        const invoiceType = normalizeValue(entry?.invoiceType);
        if (invoiceType === "ACTIVATION") return "Activation";
        if (invoiceType === "RENEWAL") return "Renewal";
        return "Invoice";
      };

      const formatEntryCycleRange = (entry) => {
        const sourceCycleNumber = safeNumber(entry?.sourceCycleNumber);
        const totalCycles = safeNumber(entry?.totalCycles);

        if (sourceCycleNumber > 0 && totalCycles > 1) {
          return `Cycle ${sourceCycleNumber}-${sourceCycleNumber + totalCycles - 1}`;
        }

        if (sourceCycleNumber > 0) {
          return `Cycle ${sourceCycleNumber}`;
        }

        if (normalizeValue(entry?.invoiceType) === "ACTIVATION" && totalCycles > 1) {
          return `Cycle 1-${totalCycles}`;
        }

        return formatCycleLabel(totalCycles);
      };

      const getEntryPaymentDate = (entry, invoice) =>
        (normalizeValue(entry?.status) === "PENDING"
          ? entry.invoiceDate || entry.createdAt || invoice.invoiceDate || invoice.createdAt
          : null) ||
        entry.paymentDate ||
        entry.paidAt ||
        invoice?.classSessionId?.[0]?.paymentDate ||
        entry.createdAt ||
        invoice.createdAt;

      // Fetch all invoice pages for this parent
      const invoiceData = [];
      let page = 1;
      let totalPages = 1;

      do {
        const response = await getAllRaiseInvoices({
          parentId,
          page,
          limit: 100,
        });
        if (!response?.success) break;

        invoiceData.push(...(response.data || []));
        totalPages = response.pagination?.totalPages || 1;
        page += 1;
      } while (page <= totalPages);

      if (invoiceData.length > 0) {
        // Format rows payment-wise so each actual payment is visible.
        const invoiceEntries = invoiceData.flatMap((invoice) =>
          getAllInvoiceEntries(invoice).map((entry) => ({
            ...entry,
            parentInvoice: invoice,
          }))
        ).filter((entry) => normalizeValue(entry?.status || entry?.parentInvoice?.status) !== "VOID");

        const formattedRows = invoiceEntries.map((entry, index) => {
          const invoice = entry.parentInvoice;
          const status = entry.status || invoice.status;
          const cycleRange = formatEntryCycleRange(entry);
          const totalClasses = safeNumber(entry.totalClasses);

          return {
            "S.No": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {index + 1}
              </ArgonTypography>
            ),
            // Date: (
            //   <ArgonTypography variant="caption" color="text" fontWeight="medium">
            //     {new Date(invoice.createdAt).toLocaleDateString("en-IN")}
            //   </ArgonTypography>
            // ),
            "Student Name": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {invoice.students && invoice.students.length > 0
                  ? invoice.students.map((s) => s.name).join(", ")
                  : "N/A"}
              </ArgonTypography>
            ),

            "Invoice ID": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {entry._id || invoice._id || "N/A"}
              </ArgonTypography>
            ),
            Type: (
              <ArgonBox display="flex" flexDirection="column">
                <ArgonTypography variant="caption" fontWeight="medium" color="text">
                  {formatInvoiceType(entry)}
                </ArgonTypography>
                <ArgonTypography variant="caption" color="secondary">
                  {[cycleRange, totalClasses > 0 ? `${totalClasses} sessions` : null]
                    .filter(Boolean)
                    .join(" - ")}
                </ArgonTypography>
              </ArgonBox>
            ),
            "Total Sessions": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {totalClasses || "0"}
              </ArgonTypography>
            ),
            "Total Amount": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                ₹ {safeNumber(entry.totalAmount).toLocaleString("en-IN")}
              </ArgonTypography>
            ),
            Status: (
              <ArgonBox
                sx={{
                  display: "inline-block",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "6px",
                  backgroundColor:
                    status === "PAID"
                      ? "#d4edda"
                      : status === "PENDING"
                      ? "#fff3cd"
                      : status === "FAILED"
                      ? "#f8d7da"
                      : "#e2e3e5",
                  color:
                    status === "PAID"
                      ? "#155724"
                      : status === "PENDING"
                      ? "#856404"
                      : status === "FAILED"
                      ? "#721c24"
                      : "#383d41",
                }}
              >
                <ArgonTypography variant="caption" fontWeight="bold" sx={{ color: "inherit" }}>
                  {status || "UNKNOWN"}
                </ArgonTypography>
              </ArgonBox>
            ),
            "Payment Date": (
              <ArgonTypography variant="caption" color="text" fontWeight="medium">
                {getEntryPaymentDate(entry, invoice)
                  ? new Date(getEntryPaymentDate(entry, invoice)).toLocaleDateString("en-IN")
                  : "Not Paid"}
              </ArgonTypography>
            ),
          };
        });

        setInvoices(formattedRows);
      }
    } catch (error) {
      console.error("Error fetching invoice history:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = () => {
    navigate(-1); // Go back to previous page
  };

  if (loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <ArgonBox display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </ArgonBox>
        <Footer />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <Card>
          <ArgonBox p={3}>
            {/* Header with Back Button */}
            <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={3}>
              <ArgonBox display="flex" alignItems="center" gap={2}>
                <ArgonButton
                  onClick={handleBackClick}
                  variant="outlined"
                  color="dark"
                  size="small"
                  sx={{
                    minWidth: "auto",
                    padding: "8px 12px",
                  }}
                >
                  <ArrowBackIcon sx={{ fontSize: 20 }} />
                </ArgonButton>
                <ArgonTypography variant="h5" fontWeight="bold">
                  Complete Invoice History
                </ArgonTypography>
              </ArgonBox>
              <ArgonTypography variant="caption" color="text">
                Total Invoices: {invoices.length}
              </ArgonTypography>
            </ArgonBox>

            {/* Table */}
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {invoices.length > 0 ? (
                <Table columns={columns} rows={invoices} />
              ) : (
                <ArgonBox p={5} textAlign="center">
                  <ArgonTypography variant="h6" color="text">
                    No invoice records found
                  </ArgonTypography>
                  <ArgonTypography variant="body2" color="secondary" mt={1}>
                    There are no invoices available for this parent.
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>
          </ArgonBox>
        </Card>
      </ArgonBox>
      <Footer />
    </DashboardLayout>
  );
}

export default InvoiceFullHistory;
